import type { Vendor } from "./vendor.type";
import type { KindCode } from "./kind-code.type";

type Result = { ok: true; rows: Vendor[] } | { ok: false; error: "vendors-invalid" };

const SLUG = /^[a-z][a-z0-9-]*$/;

/** Checks the vendors setting (comma-separated `code=Name` or `code=Name|logo-slug` items): each code lower-case words, each name filled in, a logo slug (when given) lower-case words, no code twice, at least one item. Gives the rows (the logo slug is the code when not given). */
export function verifyVendors(text: string): Result {
  const rows = text.split(",").map((item) => {
    const at = item.indexOf("=");
    const [label = "", icon = ""] = (at < 0 ? "" : item.slice(at + 1)).split("|").map((s) => s.trim());
    const code = item.slice(0, at < 0 ? undefined : at).trim();
    return { code, label, icon: icon || code };
  });
  const sound = rows.every((r) => SLUG.test(r.code) && r.label !== "" && SLUG.test(r.icon)) && new Set(rows.map((r) => r.code)).size === rows.length;
  return sound ? { ok: true, rows } : { ok: false, error: "vendors-invalid" };
}

type KindCodesResult = { ok: true; rows: KindCode[] } | { ok: false; error: "kind-codes-invalid" };

const CODE = /^[A-Z]{2,3}$/;
const TYPE = /^[a-z][a-z-]*$/;

/** Checks the kind-codes setting (comma-separated `CODE=type` items): each code 2 or 3 capital letters, each type lower-case words, no code and no type twice, at least one item. Gives the rows. */
export function verifyKindCodes(text: string): KindCodesResult {
  const rows = text.split(",").map((item) => {
    const at = item.indexOf("=");
    return { code: item.slice(0, at < 0 ? undefined : at).trim(), type: at < 0 ? "" : item.slice(at + 1).trim() };
  });
  const sound = rows.every((r) => CODE.test(r.code) && TYPE.test(r.type));
  const unique = new Set(rows.map((r) => r.code)).size === rows.length && new Set(rows.map((r) => r.type)).size === rows.length;
  return sound && unique ? { ok: true, rows } : { ok: false, error: "kind-codes-invalid" };
}
